<template>
	<div>
		<div
			class="permission-users styled-container"
			v-if="displayInternalPermissionContainer"
		>
			<div class="permission-users__header styled-container__title">
				Permission sets -
				<span
					class="permission-users__subtitle styled-container__subtitle"
				>
					{{ internalSelectedMapped }}
				</span>
			</div>
			<div class="permission-users__sets-items">
				<PermissionSet
					v-for="(item, index) in permissionSet"
					:key="index"
					:data="item"
				/>
			</div>
		</div>

		<div
			class="permission-users styled-container"
			v-if="displayConsultantPermissionContainer"
		>
			<div class="permission-users__header styled-container__title">
				Client Access Permissions -
				<span
					class="permission-users__subtitle styled-container__subtitle"
				>
					{{ consultantSelectedMapped }}
				</span>
			</div>
			<div class="permission-users__sets-items">
				<ul v-if="clientsByConsultantSelected.length">
					<li
						v-for="(item, index) in clientsByConsultantSelected"
						:key="index"
					>
						{{ item.customerName }}
					</li>
				</ul>
				<span v-else>"No clients assigned"</span>
			</div>
		</div>
	</div>
</template>

<script>
import PermissionSet from '@/site-components/permission-set/permission-set';
import { mapState, mapActions } from 'vuex';
import {
	MODULE_NAME as OPTIONS_MODULE_NAME,
	INTERNAL_ROLES_OPTS,
	CONSULTANT_ROLES_OPTS
} from '@/sections/options/options-mutation-type';
import {
	INTERNAL_ROLES,
	CONSULTANT_ROLES,
	LOAD_ROLES_WITH_PERMISSIONS
} from '@/sections/roles/roles-mutation-types';
import {
	SUPPORT_EXTERNAL,
	SUPPORT_PORTAL
} from '@/sections/users/shared/users-const.js';
import _ from 'lodash';

export default {
	name: 'UserRolesPermissions',
	model: {
		prop: 'user'
	},
	components: {
		PermissionSet
	},
	props: {
		user: {
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			consultantExternalParams: {
				page: 1,
				count: 1000,
				sortdirection: 'asc',
				active: true,
				clientId: SUPPORT_EXTERNAL
			},
			supportPortalParams: {
				page: 1,
				count: 1000,
				sortdirection: 'asc',
				active: true,
				clientId: SUPPORT_PORTAL
			}
		};
	},
	async created() {
		await this.fetchRolesWithPermissions(this.supportPortalParams);

		await this.fetchRolesWithPermissions(this.consultantExternalParams);
	},
	computed: {
		...mapState(OPTIONS_MODULE_NAME, [
			INTERNAL_ROLES_OPTS,
			CONSULTANT_ROLES_OPTS
		]),
		...mapState('roles', [INTERNAL_ROLES, CONSULTANT_ROLES]),

		permissionSet() {
			const findUniquePermissions = (roles, roleIds) => {
				const permissionIds = new Set();
				const uniquePermissions = [];

				roles.forEach(role => {
					if (
						roleIds.includes(role.roleId) &&
						!_.isEmpty(role.permissions)
					) {
						role.permissions.forEach(permission => {
							if (
								permission &&
								permission.permissionId &&
								!permissionIds.has(permission.permissionId)
							) {
								permissionIds.add(permission.permissionId);
								uniquePermissions.push(permission);
							}
						});
					}
				});

				return uniquePermissions;
			};

			const { internalIds } = this.user;
			let ps = [];

			if (_.isEmpty(internalIds)) {
				return ps;
			}

			const { items } = this[INTERNAL_ROLES];
			let permissionIds = [];

			if (!_.isEmpty(items)) {
				permissionIds = findUniquePermissions(items, internalIds);
			}

			if (!_.isEmpty(permissionIds)) {
				// Group permissions by permisson bassed permission.code
				const groupedPermissions = permissionIds.reduce(
					(acc, permission) => {
						const [key, code] = permission.permissionCode.split(
							'.'
						);
						const mappedPermission = {
							id: permission.permissionId,
							code: code,
							name: permission.permissionName
						};

						if (!acc[key]) {
							acc[key] = [];
						}

						acc[key].push(mappedPermission);
						return acc;
					},
					{}
				);

				const checkPermissions = groupedPermissions => {
					const result = [];
					const allPermissions = [
						'read',
						'update',
						'create',
						'delete'
					];

					for (const key in groupedPermissions) {
						const codes = groupedPermissions[key].map(
							item => item.code
						);
						const availablePermissions = allPermissions.filter(
							permission => codes.includes(permission)
						);

						if (
							availablePermissions.length ===
							allPermissions.length
						) {
							result.push({
								groupName: key,
								permissionName: 'all access'
							});
						} else {
							result.push({
								groupName: key,
								permissionName: availablePermissions.join(', ')
							});
						}
					}

					return result;
				};

				const orderedGroupedPermissions = _.fromPairs(
					_.sortBy(_.toPairs(groupedPermissions))
				);

				ps = checkPermissions(orderedGroupedPermissions);
			}

			return ps;
		},
		displayInternalPermissionContainer() {
			return !_.isEmpty(this.internalRolesSelected);
		},
		internalSelectedMapped() {
			if (!_.isEmpty(this.internalRolesSelected)) {
				return this.internalRolesSelected
					.map(role => role.text)
					.join(', ');
			}
			return '';
		},
		internalRolesSelected() {
			const { items } = this[INTERNAL_ROLES_OPTS];
			const { internalIds } = this.user;
			if (!_.isEmpty(items) && !_.isEmpty(internalIds)) {
				return this.filterRolesByIds(items, internalIds);
			}
			return [];
		},
		displayConsultantPermissionContainer() {
			return !_.isEmpty(this.consultantRolesSelected);
		},
		consultantRolesSelected() {
			const { items } = this[CONSULTANT_ROLES_OPTS];
			const { consultantIds } = this.user;
			if (!_.isEmpty(items) && !_.isEmpty(consultantIds)) {
				return this.filterRolesByIds(items, consultantIds);
			}
			return [];
		},
		consultantSelectedMapped() {
			if (!_.isEmpty(this.consultantRolesSelected)) {
				return this.consultantRolesSelected
					.map(role => role.text)
					.join(', ');
			}
			return '';
		},
		clientsByConsultantSelected() {
			const findClientsByRoleIds = (roles, roleIds) => {
				const customerIds = new Set();
				const uniqueCustomers = [];

				roles.forEach(role => {
					if (
						roleIds.includes(role.roleId) &&
						Array.isArray(role.customers)
					) {
						role.customers.forEach(customer => {
							if (
								customer &&
								customer.customerId &&
								!customerIds.has(customer.customerId)
							) {
								customerIds.add(customer.customerId);
								uniqueCustomers.push(customer);
							}
						});
					}
				});

				return uniqueCustomers;
			};

			let clients = [];
			let { consultantIds } = this.user;
			const { items } = this[CONSULTANT_ROLES];

			if (_.isEmpty(consultantIds)) {
				return clients;
			}

			if (!_.isEmpty(items)) {
				clients = findClientsByRoleIds(items, consultantIds);
			}

			return clients;
		}
	},
	methods: {
		...mapActions('roles', {
			fetchRolesWithPermissions: LOAD_ROLES_WITH_PERMISSIONS
		}),

		filterRolesByIds(roles, ids) {
			return roles.reduce((acc, item) => {
				if (ids.includes(Number(item.value))) {
					acc.push(item);
				}
				return acc;
			}, []);
		}
	}
};
</script>

<style lang="scss" scoped>
.permission-users {
	margin-bottom: $gutter-lg;

	&__sets-items {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
		column-gap: $gutter-xl;
		row-gap: $gutter-xl;
		padding: $padding-md;
	}
}

@include mq($from: md) {
	.permission-users {
		&__role-field {
			grid-column-start: 1;
			grid-column-end: 3;
		}
		&__status-field {
			padding-bottom: 0;
		}
	}
}
@include mq($from: lg) {
	.permission-users {
		width: 744px;

		&__role-field {
			grid-column-start: 1;
			grid-column-end: 2;
		}
	}
}
</style>
